/**
 * AdvisorBioEditor — small two-line tagline shown next to the advisor
 * headshot on the buyer's /my-matches "My Advisor" panel. Persisted on
 * the dealer document via PUT /api/dealer/profile.
 *
 * Keeps things minimal — 160 char cap, auto-saves on blur (or explicit
 * Save button click), toast on success.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, UserCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 160;

const AdvisorBioEditor = ({ dealer, onChange, isDark }) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(dealer?.advisor_bio || ''); }, [dealer?.advisor_bio]);

  const dirty = (value || '') !== (dealer?.advisor_bio || '');

  const save = async () => {
    if (!dirty) return;
    if ((value || '').length > MAX) {
      toast.error(`Keep the tagline under ${MAX} characters.`);
      return;
    }
    setSaving(true);
    try {
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        sessionStorage.getItem('token');
      await axios.put(
        `${API}/dealer/profile`,
        { advisor_bio: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Advisor tagline saved');
      onChange?.(value);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-2xl ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-gray-200'} p-5 sm:p-6 shadow-sm`}
      data-testid="advisor-bio-editor"
    >
      <div className="flex items-center gap-2 mb-3">
        <UserCircle2 className="w-5 h-5 text-[#F2A900]" />
        <h3 className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Advisor Tagline
        </h3>
        <span className={`ml-auto text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Two lines about you, shown next to your headshot
        </span>
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value.slice(0, MAX))}
        onBlur={save}
        placeholder="e.g. Michael, 12 years selling travel trailers. Ask me anything about tow limits, floorplans, or financing."
        rows={2}
        maxLength={MAX}
        className={`w-full rounded-xl px-3 py-2 text-sm ${
          isDark ? 'bg-gray-950 border border-gray-700 text-white placeholder:text-gray-500' : 'bg-white border border-gray-300 placeholder:text-gray-400'
        } focus:outline-none focus:ring-2 focus:ring-amber-400/40`}
        data-testid="advisor-bio-input"
      />
      <div className="mt-2 flex items-center justify-between">
        <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {(value || '').length}/{MAX}
        </span>
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          size="sm"
          className="bg-[#004E86] hover:bg-[#003d6b] text-white font-semibold disabled:opacity-60"
          data-testid="advisor-bio-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
        </Button>
      </div>
    </div>
  );
};

export default AdvisorBioEditor;
